import type { WebinarPageContent } from './webinar/types';
import { buildCachingOptimizerWebinar } from './webinar/events/build-caching-optimizer-11-26';

export const eventTypes = {
  online: 'Online',
  inPerson: 'In-person',
} as const;

export type FilterTopic =
  | 'Developer productivity'
  | 'Acceleration'
  | 'Troubleshooting'
  | 'Metrics'
  | 'Gradle Build Tool'
  | 'Apache Maven'
  | 'sbt'
  | 'Android'
  | 'AI'
  | 'Software Delivery'
  | 'DORA'
  | 'GRC';

export type EventType = (typeof eventTypes)[keyof typeof eventTypes];
export type Speaker = {
  name: string;
  title: string;
  company: string;
  bio: string;
  headshotLink?: string;
  // Role on the event, e.g. 'Host' or 'Co-Host'. Shown above the name by the webinar template only.
  label?: string;
};

export type BaseEvent = {
  title: string;
  description: string;
  eventType: EventType;
  date: string;
  time: string;
  timeStamp: string; //  ISO 8601
  locationShort?: string;
  isFree: boolean;
  topics?: FilterTopic[];
  locationFull?: string;
};

type RegisterForm = {
  pardotFormSubmitLink: `https://${string}`;
  zoomId: string;
};

export type InternalEvent = BaseEvent & {
  videoLink?: string;
  externalLink?: never;
  slug: string;
  fullDescription: string | React.ReactElement;
  discussionTopics: string[];
  locationFull?: string;
  speakers: Speaker[];
  heroImageLink: string;
  // Optional 16:9 hero art for phones, in the same folder as heroImageLink. Only the
  // webinar template reads it; see src/sections/events/webinar/README.md.
  heroImageMobileLink?: string;
  // Direct-link only (e.g. customer-only webinars): kept off the events listing, sitemap and
  // past-event grids, and noindexed, until the event is past AND has a recording (videoLink).
  // See isListedEvent in ./utils.
  unlistedUntilRecording?: boolean;
  // Opt-in to the webinar template (src/sections/events/webinar). Omit for the legacy layout.
  webinarPage?: WebinarPageContent;
  // This intersection type ensures an InternalEvent either has both registration fields (pardotFormSubmitLink & zoomId)
  // or neither of them by using 'never' type to make the fields unavailable
} & (RegisterForm | { pardotFormSubmitLink?: never; zoomId?: never });

export type ExternalEvent = BaseEvent & {
  externalLink?: string;
};

export type Event = InternalEvent | ExternalEvent;

export const isInternalEvent = (event: Event): event is InternalEvent => {
  return 'slug' in event;
};

export const events: Event[] = [
  {
    slug: 'developer-productivity-Apache-Maven-18-hacks-accelerate-builds-and-tests-01-25',
    title: 'Developer Productivity with Apache Maven™:\n18 hacks to accelerate your builds and tests',
    description:
      'Join Brian Demers as he shares a comprehensive list of hacks to make your Maven experience more productive and less miserable.',
    eventType: 'Online',
    date: 'January 23, 2025',
    time: '08:00 PST',
    timeStamp: '2025-01-23T17:00:00+01:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity', 'Apache Maven', 'Acceleration'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2024-12-09/n3v8qy',
    zoomId: '83889353471',
    fullDescription:
      "If you're a regular Apache Maven user, you know that build times can quickly add up, eating into your development time and slowing down your feedback loop—whether you're waiting for a local build to finish or watching a pull request on CI inch towards merge, every minute counts.\n\nIn this webinar by Brian Demers, Java Champion and veteran Apache Maven contributor, we will review a comprehensive list of tips and hacks to make your Maven experience more productive and less miserable. \n\nSome of these tips are low-hanging fruit for even Maven beginners, others require more thought and planning for Maven experts. Either way, following this advice will make a meaningful difference to your developer productivity—plus, being able to observe and report productivity boosts to your management team is always a good thing!",
    heroImageLink: 'EVENT_mavenHacksWebinar-JAN2025.jpg',
    discussionTopics: [
      'Sensible Maven tips and tricks learned over 15 years (low hanging fruit)',
      'Demo: the power of build caching, demonstrated with Apache SCIMple',
      'Leveraging observability to manage "Maven dependency hell"',
      'Your toughest questions for the Q&A',
    ],
    speakers: [
      {
        name: 'Brian Demers',
        title: 'Developer Advocate',
        company: 'Gradle',
        bio: 'Brian Demers is a Developer Advocate at Gradle, a Java Champion, and an Apache Member who contributes to the Directory, Maven, and Shiro projects. He spends much of his day contributing to OSS projects by writing code, tutorials, blogs, and answering questions. In addition to typical software development, Brian also has a passion for fast builds and automation.\n\nAway from the keyboard, Brian is a beekeeper and can likely be found playing board games. You can find him on Twitter at https://twitter.com/briandemers.',
        headshotLink: 'brianDemers.png',
      },
    ],
    videoLink: 'https://www.youtube.com/watch?v=280o9AupjFI',
  },
  {
    slug: 'writing-tests-is-hard-fixing-them-is-harder-11-24',
    title: 'Writing tests is hard. Fixing them is harder.',
    description:
      'Learn several techniques for troubleshooting tests and understand what information you need to identify problems.',
    eventType: 'Online',
    date: 'November 13, 2024',
    time: '16:00 GMT',
    timeStamp: '2024-11-13T16:00:00+00:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity', 'Acceleration', 'Troubleshooting'],
    fullDescription:
      "Testing is a Good Thing, right? Especially automated testing. But the phrase 'good things come to those who wait' doesn't appeal to a busy developer. When a test starts failing, dropping everything to find and fix the problem can be a frustrating experience.\n\nIn this webinar, Trisha Gee will cover several techniques for troubleshooting tests and go through some of the information you need to identify problems. She will also rant about flaky tests, and how toxic they are to developer productivity. Then she'll share some tips on writing tests, with an eye to making troubleshooting and debugging easier. Finally, she'll do a live coding demonstration and offer up some 'best practices'. Oh, and AI & machine learning will be mentioned at some point.",
    discussionTopics: [
      'Why is it hard to troubleshoot tests?',
      'What steps should you take to fix a test?',
      'Why are flaky tests toxic?',
      'How can you write tests that are easier to fix?',
    ],
    speakers: [
      {
        name: 'Trisha Gee',
        title: 'Lead Technical Evangelist',
        company: 'Gradle',
        bio: 'Trisha is a Java Champion, software engineer, developer advocate and author who is passionate about developer productivity and happiness. She has expertise in Java high performance systems and dabbles with Open Source development.',
        headshotLink: 'trishaGee.png',
      },
    ],
    heroImageLink: 'EVENT_testsWebinar-NOV2024.jpg',
    videoLink: 'https://www.youtube.com/watch?v=I7H2EtVvX4c',
  },
  {
    slug: 'scala-developers-crack-open-the-black-box-of-sbt-10-24',
    title: 'Scala developers: Crack open the black box of sbt with build and test observability',
    description:
      'Learn how Scala developers using sbt can finally observe everything that happens during the build and test feedback cycle and reap tangible developer productivity gains.',
    eventType: 'Online',
    date: 'October 10, 2024',
    time: '15:00 GMT',
    timeStamp: '2024-10-10T15:00:00+00:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity', 'Metrics', 'sbt'],
    fullDescription:
      "The Scala ecosystem has many OSS technologies, yet it's underserved when it comes to enterprise tooling. Scala developers rarely have actionable build insights when something goes wrong with sbt.\n\nUntil now! Develocity Build Scan® offers Scala developers deep insights into what's going on inside the black box of sbt—including shareable links to specific build failures, flaky tests, performance regressions, and more.\n\nJoin Iulian Dragos to learn how Scala developers using sbt can finally observe everything that happens during the build and test feedback cycle, leading to tangible developer productivity gains.",
    discussionTopics: [
      'Build insights like long-running tasks, bottlenecks, and common failure patterns',
      'Flaky and slow test identification and resolution',
      'Long-term performance trends and analysis',
      'Bonus: how Build Cache for sbt reduced Scala 3 builds from 20-minutes to 90-seconds',
    ],
    speakers: [
      {
        name: 'Iulian Dragos',
        title: 'Principal Software Engineer',
        company: 'Gradle',
        bio: 'Iulian is a seasoned software engineer with over a decade of experience in the Scala programming language. He was part of the original Scala team, where he made significant contributions to the design and implementation of the language. He is a regular speaker at conferences and meetups, and he is also an active contributor to open source projects related to Scala.',
        headshotLink: 'iulianDragos.png',
      },
    ],
    heroImageLink: 'EVENT_scalaWebinar-OCT2024.jpg',
    videoLink: 'https://www.youtube.com/watch?v=FMWEj4gaEJQ',
  },
  {
    slug: 'gradle-9-is-coming-whats-in-it-for-developers-09-24',
    title: "Gradle 9 is coming: What's in it for developers?",
    description:
      "Join Louis Jacomet, Senior Lead Software Engineer at Gradle, to learn more about what's coming in Gradle 9.",
    eventType: 'Online',
    date: 'September 24, 2024',
    time: '15:00 GMT',
    timeStamp: '2024-09-24T15:00:00+00:00',
    locationShort: '',
    isFree: true,
    topics: ['Gradle Build Tool', 'Developer productivity'],
    fullDescription:
      "The release of Gradle Build Tool 9.0 is approaching—so what should developers know?\n\nGradle 9 is packed with features that continue to evolve the developer UX. One focus is on scalability—making builds and tests as fast as possible, even for massive projects. Another focus is on comprehensibility—making Gradle easier to adopt and use for teams of any size and skill level.\n\nJoin Louis Jacomet, Senior Lead Software Engineer at Develocity, conference speaker and community organizer, to learn more about what's coming in Gradle 9.",
    discussionTopics: [
      'Continued investment in configuration caching',
      'Upcoming isolated projects feature',
      'Higher level modeling of software projects',
      'Separation of concerns for build users and build authors',
    ],
    speakers: [
      {
        name: 'Louis Jacomet',
        title: 'Sr. Lead Software Engineer',
        company: 'Gradle',
        bio: "Louis has been perfecting his knowledge of Java for 20+ years. A developer at heart, his role has evolved to include technical team leading, coaching, and management. In addition to the bits and bytes, he's interested in creating productive project teams. After working for Terracotta, he's now part of the Gradle Build Tool team.",
        headshotLink: 'louisJacomet.png',
      },
    ],
    heroImageLink: 'EVENT_gradle9Webinar-SEP2024.jpg',
    videoLink: 'https://www.youtube.com/watch?v=FBCqeY6AhMM',
  },
  {
    title: 'DPE Summit',
    description:
      'Two days, dozens of sessions, countless learnings. Join our annual event dedicated to Developer Productivity Engineering and Developer Experience.',
    eventType: 'In-person',
    date: 'September 24, 2024',
    time: 'N/A',
    timeStamp: '2024-09-24T00:00:00+00:00',
    locationShort: 'San Francisco, CA',
    isFree: false,
    externalLink: 'https://dpe.org/summit2024/',
    topics: ['Developer productivity', 'Acceleration', 'Troubleshooting', 'Metrics'],
  },
  {
    slug: 'gradle-artifact-transforms-and-deeper-build-insights-08-24',
    title: 'Gradle Artifact Transforms and deeper build insights',
    description:
      'Get the basics of Artifact Transforms and then explore real-world use cases where Artifact Transforms insights from a Develocity Build Scan® can be used to identify and address bottlenecks in your build process.',
    eventType: 'Online',
    date: 'August 15, 2024',
    time: '15:00 GMT',
    timeStamp: '2024-08-15T15:00:00+00:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity', 'Acceleration', 'Troubleshooting', 'Gradle Build Tool'],
    fullDescription:
      "Artifact Transforms play a crucial role in streamlining your development workflow, yet they're often overlooked as a mechanism for boosting developer productivity. In this webinar, we'll introduce the basics of Artifact Transforms and demo the initial steps of implementing a simple artifact transform. Next, we'll show how integrating this with Gradle's caching mechanism can help reduce build times. Finally, we'll look at real-world use cases where Artifact Transforms insights from a Develocity Build Scan® can be used to pinpoint and rapidly address bottlenecks in your build process.",
    discussionTopics: [
      'Introducing the basics of Artifact Transforms',
      'Demo implementation of a simple artifact transform',
      "How to integrate this with Gradle's caching mechanism",
      'Tips for making Artifact Transforms a more effective and efficient part of your build process',
      'Real-world use cases of using Build Scan insights on Artifact Transforms to address build process bottlenecks',
    ],
    speakers: [
      {
        name: 'Nelson Osacky',
        title: 'Lead Solutions Engineer',
        company: 'Gradle',
        bio: 'Nelson Osacky is a former Square employee based out of San Francisco. He was also part of SoundCloud in Berlin. Now he is speeding up builds around the world at Gradle.',
        headshotLink: 'nelsonOsacky.png',
      },
      {
        name: 'Inaki Villar',
        title: 'Sr. Solutions Engineer',
        company: 'Gradle',
        bio: 'Based in Los Angeles, Inaki is working on providing solutions and optimizations for clients of Develocity in Gradle/Maven builds. Before this role, Inaki focused on Android and Build performance for companies like Tinder, Agoda, Synchronous, Ryanair and Ideaknow. Originally from Mallorca, Spain, he enjoys watching real football and watersports.',
        headshotLink: 'inakiVillar.png',
      },
    ],
    heroImageLink: 'EVENT_artifactTransformsWebinar-AUG2024.jpg',
    videoLink: 'https://www.youtube.com/watch?v=VmRvCHm2ip4',
  },
  {
    title: 'DPE Tour: Washington D.C.',
    description:
      'Join us for an afternoon focused on Developer Productivity Engineering and Developer Experience with talks from Capital One, Airbnb, and American Airlines.',
    eventType: 'In-person',
    date: 'July 18, 2024',
    time: '19:00 GMT',
    timeStamp: '2024-07-18T19:00:00+00:00',
    locationShort: 'Arlington, VA',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-washington-dc-tour/',
    topics: ['Developer productivity'],
  },
  {
    title: 'How Quarkus accelerates merging Apache Maven PR builds with Develocity',
    description: '',
    eventType: 'Online',
    date: 'July 11, 2024',
    time: '17:00 GMT',
    timeStamp: '2024-07-11T17:00:00+00:00',
    locationShort: '',
    isFree: true,
    externalLink: 'https://dpe.org/lowdown/how-quarkus-accelerates-merging-maven-pr-builds-with-develocity',
    topics: ['Apache Maven', 'Acceleration'],
  },
  {
    title: 'DPE Tour: Dallas',
    description:
      'Join us in Dallas for an afternoon focused on Developer Productivity Engineering and Developer Experience, with talks from Airbnb, Netflix, and American Airlines.',
    eventType: 'In-person',
    date: 'March 7, 2024',
    time: '15:00 CDT',
    timeStamp: '2024-03-07T15:00:00-06:00',
    locationShort: 'Plano, TX',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-dallas-tour/',
    topics: ['Developer productivity', 'Troubleshooting'],
  },
  {
    title: 'DPE Tour: Seattle',
    description:
      'Join us in Seattle for an afternoon focused on Developer Productivity Engineering and Developer Experience, with talks from Microsoft and Meta.',
    eventType: 'In-person',
    date: 'March 18, 2024',
    time: '15:00 PST',
    timeStamp: '2024-03-18T15:00:00-08:00',
    locationShort: 'Seattle, WA',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-seattle-tour/',
    topics: ['Developer productivity', 'Metrics', 'Acceleration'],
  },
  {
    title: 'DPE Tour: Atlanta',
    description:
      'Join us at DevNexus in Atlanta for an afternoon focused on Developer Productivity Engineering and Developer Experience, with talks from Netflix and Gradle.',
    eventType: 'In-person',
    date: 'April 9, 2024',
    time: '13:30 EDT',
    timeStamp: '2024-04-09T13:30:00-04:00',
    locationShort: 'Atlanta, GA',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-atlanta-tour/',
    topics: ['Developer productivity', 'Metrics'],
  },
  {
    title: 'DPE Tour: New York City',
    description:
      'Join us in the heart of New York City for an afternoon focused on Developer Productivity Engineering and Developer Experience, with talks from OpenAI, Microsoft, Netflix, and AWS.',
    eventType: 'In-person',
    date: 'April 17, 2024',
    time: '15:00 EDT',
    timeStamp: '2024-04-17T15:00:00-04:00',
    locationShort: 'New York, NY',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-new-york-tour/',
    topics: ['Developer productivity', 'Metrics', 'Troubleshooting'],
  },
  {
    title: 'DPE Tour: Berlin',
    description:
      'Join us in Berlin for an afternoon focused on Developer Productivity Engineering and Developer Experience with talks from Delivery Hero, Gradle, and Zalando.',
    eventType: 'In-person',
    date: 'June 11, 2024',
    time: '15:00 CST',
    timeStamp: '2024-06-11T15:00:00+02:00',
    locationShort: 'Berlin, Germany',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-berlin-tour/',
    topics: ['Developer productivity', 'Metrics', 'Troubleshooting'],
  },
  {
    title: 'DPE Tour: Boston',
    description:
      'Join us in Boston for an afternoon focused on Developer Productivity Engineering and Developer Experience, with talks from Toast, Netflix, and Meta.',
    eventType: 'In-person',
    date: 'June 12, 2024',
    time: '12:30 EDT',
    timeStamp: '2024-06-12T12:30:00-04:00',
    locationShort: 'Boston, MA',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-boston-tour/',
    topics: ['Developer productivity', 'Metrics', 'Troubleshooting'],
  },
  {
    title: 'DPE Tour: London',
    description:
      'Join us in London for an afternoon focused on Developer Productivity Engineering and Developer Experience, with talks from Red Hat, Netflix, Gradle, Meta, American Airlines and Spotify.',
    eventType: 'In-person',
    date: 'June 25, 2024',
    time: '15:30 BST',
    timeStamp: '2024-06-25T15:30:00+01:00',
    locationShort: 'London, England',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-london-tour/',
    topics: ['Developer productivity', 'Metrics', 'Troubleshooting'],
  },
  {
    title: 'DPE Tour: San Francisco',
    description:
      'Join us for a three hour event focused on Developer Productivity Engineering and Developer Experience at Pinterest HQ.',
    eventType: 'In-person',
    date: 'January 23, 2025',
    time: '15:00 PST',
    timeStamp: '2025-01-23T15:00:00-08:00',
    locationShort: 'San Francisco, CA',
    isFree: true,
    topics: ['Developer productivity', 'Acceleration', 'Troubleshooting', 'Metrics'],
    externalLink: 'https://dpe.org/dpe-san-francisco-tour-2025/',
  },
  {
    slug: 'observing-build-ci-productivity-oss-projects',
    heroImageLink: 'observing-build-ci-productivity-oss-projects.jpg',
    title: 'Observing build and CI productivity in your favorite OSS projects',
    description: "We'll share insights gained from the data from over 50k OSS project builds/wk.",
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-01-29/n3vqf1',
    zoomId: '85651539459',
    eventType: 'Online',
    date: 'February 27, 2025',
    time: '08:00 PST',
    timeStamp: '2025-02-27T08:00:00-08:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity', 'Metrics', 'Gradle Build Tool'],
    fullDescription:
      "If you use Spring Framework, JUnit Platform, Testcontainers, or Hibernate, then you're using open-source software that leverages Develocity for accelerated builds/tests and observability into failures, flaky tests, performance regressions, and more.\n\nWe're proud to sponsor free (public) instances of Develocity for over 20 well-known open-source projects, including those from the Apache Software Foundation, Spring Framework, Commonhaus Foundation, Micronaut Foundation, Kotlin Foundation, Scala Center, and many more.\n\nIn this webinar with Gašper Kojek, Solutions Engineer at Gradle, we'll review surprising insights gained from the data from over 50,000 OSS project builds per week.",
    discussionTopics: [
      'Key insights from analyzing 50k+ OSS project builds per week',
      'How popular OSS projects leverage build and test observability',
      'Common patterns in build/test failures and performance regressions',
      'Live demo: exploring public build data from Spring Framework, JUnit, and more',
    ],
    speakers: [
      {
        name: 'Gašper Kojek',
        title: 'Solutions Engineer',
        headshotLink: 'gašperKojek.png',
        company: 'Gradle',
        bio: 'Based in Slovenia, Gašper is a Solutions Engineer working on providing solutions and optimizations for clients of Develocity in Gradle/Maven builds. Coming from an Android background, he is no stranger to long build times and how to improve them. He worked with a diverse Android stack across multiple projects, as well Docker, Jenkins, Github Actions and Develocity, after which he found himself in helping others at Gradle.',
      },
    ],
    videoLink: 'https://www.youtube.com/watch?v=rwfNfLLWvnM',
  },
  {
    title: 'DPE Tour: Palo Alto',
    description:
      'Join us for a four hour event focused on Developer Productivity Engineering and Developer Experience at Meta HQ.',
    eventType: 'In-person',
    date: 'March 25, 2025',
    time: '14:00 PST',
    timeStamp: '2025-03-25T14:00:00-08:00',
    locationShort: 'Menlo Park, CA',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-palo-alto-tour-2025/',
    topics: ['Developer productivity', 'Acceleration', 'Troubleshooting', 'Metrics'],
  },
  {
    slug: 'troubleshooting-gradle-maven-builds-develocity-03-27',
    title: 'Troubleshooting Gradle and Maven builds with Develocity',
    description:
      'In this talk by Stefan Wolf, Principal Software Engineer at Gradle, we take a look at troubleshooting local and CI builds with Gradle and Apache Maven.',
    eventType: 'Online',
    date: 'March 27, 2025',
    time: '08:00 PST',
    timeStamp: '2025-03-27T08:00:00-07:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity', 'Troubleshooting', 'Apache Maven', 'Gradle Build Tool'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-02-25/n3w41b',
    zoomId: '85161564709',
    fullDescription:
      "How much time do you spend each day troubleshooting your builds and tests? \n\nIf you're like most developers, you regularly see dependencies fail, infrastructure explode, non-stop flaky tests, and Checkstyle turning into Gandalf (\"you shall not pass\"). That's ok, right? At least, everything works on your machine. \n\nSarcasm aside, there are many points of failure. We need proper tools to identify and fix the root cause as quickly and easily as possible.\n\nIn this talk by Stefan Wolf, Principal Software Engineer at Gradle, we take a look at troubleshooting local and CI builds with Gradle and Apache Maven. We'll dive deep into observing what is actually happening when various build and test failures arise, and the tools at your disposal for solving them rapidly.",
    discussionTopics: [
      'Using (free) Develocity Build Scan® for Gradle and Maven to provide an X-ray of your project',
      'Use cases of well-known Gradle and Maven OSS projects using Develocity and the proven benefits they see',
      'Develocity demos featuring Gradle Build Tool and Quarkus, diving into observability and troubleshooting tactics',
    ],
    speakers: [
      {
        name: 'Stefan Wolf',
        title: 'Principal Software Engineer',
        company: 'Gradle',
        bio: 'Stefan Wolf started his programming career more than fifteen years ago, and now spends his time as Principal Software Engineer at Gradle. Stefan has been an integral part of the Gradle Build Tool team for the last nine years, which has delivered Build Cache and other features to improve build performance. More recently, Stefan has focused on Develocity Build Scan®, leading the team that delivered artifact transforms and resource usage insights, to help developers troubleshoot builds. As co-maintainer of the popular Jenkins Gradle plugin, Stefan is constantly looking for better automation tools and pathways to save developers time and increase developer joy.',
        headshotLink: 'stefanWolf.jpg',
      },
    ],
    videoLink: 'https://youtu.be/ncFm83La0d4',
    heroImageLink: 'troubleshooting-gradle-maven-builds-develocity.jpg',
  },
  {
    title: 'DPE Summit 2025',
    description: 'The only event dedicated to Developer Productivity Engineering (DPE) and Developer Experience.',
    eventType: 'In-person',
    date: 'September 23-24, 2025',
    time: '09:00 PDT',
    timeStamp: '2025-09-23T09:00:00-07:00',
    locationShort: 'San Francisco, CA',
    isFree: false,
    externalLink: 'https://dpe.org/dpe-summit',
    topics: ['Developer productivity', 'Acceleration', 'Troubleshooting', 'Metrics', 'Gradle Build Tool', 'Android'],
  },
  {
    slug: 'failure-summaries-webinar-develocity-04-16',
    title: 'Why did my build fail? Using AI to troubleshoot faster with Failure Summaries',
    description:
      "In this session with Laurent Ploix, Senior Product Manager at Gradle, you'll see how Failure Summaries can streamline troubleshooting with a deep dive into how to distill complex test failures.",
    eventType: 'Online',
    date: 'April 16, 2025',
    time: '08:00 PST',
    timeStamp: '2025-04-16T08:00:00-07:00',
    locationShort: '',
    isFree: true,
    topics: ['Troubleshooting', 'Metrics', 'Developer productivity'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-03-11/n3wc2q',
    zoomId: '87681701005',
    fullDescription:
      'Most developers tell us that working with CI is frustrating in large projects. Making a change in large projects can generate hundreds of builds with various types of failures—not to mention overall background flakiness. When something goes wrong, troubleshooting issues requires manually navigating several UIs and many large log files.\n\nThis is where "Failure Summaries", an upcoming Develocity feature, comes in to save the day. Built in-house for Gradle engineers, Failure Summaries use AI for categorization and clustering to determine which tests are primarily responsible for a number of failures.\n\nIn this session with Laurent Ploix, Senior Product Manager at Gradle, you\'ll see how Failure Summaries can streamline troubleshooting with a deep dive into how to distill complex test failures.',
    discussionTopics: [
      'What measuring developer productivity looks like, which metrics matter, and how we want to simplify troubleshooting',
      'The effectiveness of extracting CI feedback through the lens of specific evaluation metrics, for example, error log segments, failure clustering into logical groups, root cause identification, and pinpointing infrastructure issues',
      "Concrete examples are seen in action in the Gradle team's own CI environment.",
    ],
    videoLink: 'https://youtu.be/-B5EubBu4Vw',
    speakers: [
      {
        name: 'Laurent Ploix',
        title: 'Senior Product Manager',
        company: 'Gradle',
        bio: "Laurent recently joined Gradle as a product manager. He joined Spotify in 2014 as a Product Manager for Continuous Integration, coinciding with the company's rapid growth. He then led the Platform Insights team, which focused on understanding productivity through a multi-disciplinary approach combining data science, data engineering, and user research.",
        headshotLink: 'laurentPloix.png',
      },
    ],
    heroImageLink: 'failure-summaries-webinar-develocity.jpg',
  },
  {
    slug: 'Declarative-Gradle-developer-first-approach-to-Gradle-builds-04-25',
    title: 'Declarative Gradle: A developer-first approach to Gradle builds',
    description:
      "In this session, we'll look at developer challenges with understanding and maintaining complex builds and demonstrate our innovative new approach.",
    eventType: 'Online',
    date: 'April 24, 2025',
    time: '08:00 PST',
    timeStamp: '2025-04-24T08:00:00-07:00',
    videoLink: 'https://youtu.be/rNknLhYel6c',
    locationShort: '',
    isFree: true,
    topics: ['Gradle Build Tool', 'Developer productivity'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-03-13/n3wd82',
    zoomId: '88630691060',
    fullDescription: (
      <>
        <p>
          As Uncle Ben once said to <s>Toby Maguire</s> Spider-Man, &quot;With great power comes great
          responsibility&quot;. Here at Gradle we like to say, &quot;With great flexibility comes great power&quot;. And
          with that flexibility comes a learning curve that bends users toward comprehensibility and maintainability.
        </p>
        <br />
        <p>
          This is why we introduced Declarative Gradle in 2024. Now in its third EAP version, this experimental project
          aims to provide an elegant and extensible declarative build language that enables expressing any build in a
          clear and understandable way. So what does Declarative Gradle mean for developers?
        </p>
        <br />
        <p>
          In this session by Paul Merlin and Sterling Greene, long-time software engineers at Gradle, we look at
          developer challenges with understanding and maintaining complex builds. We&apos;ll demonstrate an innovative
          approach that harnesses the expressiveness of Kotlin while imposing restrictions to enhance clarity and
          maintainability. By the end, you&apos;ll better understand the benefits a declarative paradigm for build
          definition brings to project workflows.
        </p>
      </>
    ),
    heroImageLink: 'gbt-webinar-gradle-first-builds.jpg',
    discussionTopics: [
      'Declarative build principles: Understand the advantages of declarative build language and its impact on project maintainability, readability, and collaboration.',
      "Expressive DSL: Explore the DSL's capabilities and how it is a powerful tool for concisely expressing build configuration.",
      'Impact on projects: Explore case studies of projects that use declarative build language, showcasing improvements in understanding, and maintenance.',
    ],
    speakers: [
      {
        name: 'Paul Merlin',
        title: 'Software Engineer',
        company: 'Gradle',
        bio: 'Paul is an experienced, self-made, software engineer with a wide knowledge ranging from computers, network security, and architectures to high-level programming languages, domain modeling, and cryptography. A hobbyist at 7 already, Paul professionally started to build software in 2000.\n\n He joined the Gradle team nine years ago, on time for 2.14, participated in the big performance efforts that went into the 3.x and 4.x versions, led the Gradle Kotlin DSL and Configuration Cache teams since then and is now working on making Gradle more declarative.',
        headshotLink: 'paulMerlin.png',
      },
      {
        name: 'Sterling Greene',
        title: 'Software Engineer',
        company: 'Gradle',
        bio: 'Sterling Greene is a Senior Lead Software Engineer for Gradle. He has helped lead transformative change in large organizations to improve software quality, delivery speed and development costs. Recently, he has been focused on making Gradle easier to use. When he’s not working on Gradle, he spends time making pizzas for his family.',
        headshotLink: 'sterlingGreene.png',
      },
    ],
  },
  {
    slug: 'gradle-configuration-cache-05-25',
    title: 'Gradle Configuration Cache: 2x faster, 4x smaller, and ready for Gradle 9.0',
    description:
      'Join Alex Semin, Staff Software Engineer at Gradle, for a presentation on the current state of Configuration Cache: its benefits, functionality, value, and recent improvements in performance, resource use, and developer experience.',
    eventType: 'Online',
    date: 'May 15, 2025',
    time: '08:00 PST',
    timeStamp: '2025-05-15T08:00:00-07:00',
    locationShort: '',
    isFree: true,
    topics: ['Gradle Build Tool'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-04-16/n3wwjx',
    zoomId: '84735011363',
    fullDescription:
      "If you use Gradle Build Tool, then you probably know that each build includes a configuration phase that evaluates the build scripts of all participating projects and creates a task graph for requested tasks. \n\nIn large codebases, this configuration time can be a significant part of the build—even longer than the execution phase—which disrupts developer flow and productivity. What if we could cache the configuration result for reuse in later builds?\n\nGradle Configuration Cache offers exactly this. It's a popular feature that will become the preferred method of execution in Gradle 9.0. For Java/Kotlin, Android, or native projects, it can drastically improve configuration times by 2X and reduce cache size by 4X—especially valuable for builds cold-starting on CI.\n\nJoin Alex Semin, Staff Software Engineer at Gradle, for a presentation on the current state of Configuration Cache: its benefits, functionality, value, and recent improvements in performance, resource use, and developer experience.",
    discussionTopics: [
      'How to leverage Configuration Cache in your Gradle projects using improved tooling and better integrations with Problems API and Build Scan®.',
      'Examples and metrics from open-source projects that have already adopted Configuration Cache (including Gradle Build Tool itself).',
      "What's next in the upcoming releases and how Gradle users can prepare for it to be provided as a default option in the near future.",
    ],
    videoLink: 'https://youtu.be/SW0x_oRXbYw',
    speakers: [
      {
        name: 'Alex Semin',
        title: 'Staff Software Engineer',
        company: 'Gradle',
        bio: 'Alex is an enthusiastic team leader and quality-focused developer with a passion for building tools and programming languages. With over 10 years of experience in software engineering, Alex is currently part of the Gradle Build Tool team. He focuses on aspects of scalability and adaptability of Gradle to builds of any size and shape. With the goal of putting “soft” back into software, he is committed to the life-long learning and greatly enjoys sharing his knowledge with others.',
        headshotLink: 'alexSemin.png',
      },
    ],
    heroImageLink: 'gbt-webinar-config-cache.png',
  },
  {
    title: 'DPE Tour: San Jose',
    description:
      'Join us for this afternoon event focused on Developer Productivity Engineering and Developer Experience at eBay HQ.',
    eventType: 'In-person',
    date: 'May 22, 2025',
    time: '15:00 PST',
    timeStamp: '2025-05-22T15:00:00-07:00',
    locationShort: 'San Jose, CA',
    isFree: true,
    externalLink: 'https://dpe.org/dpe-san-jose-tour/',
    topics: ['Developer productivity'],
  },
  {
    slug: 'testing-software-is-awful-05-25',
    title: 'Testing software is awful: Here is how we can fix it',
    description:
      'Join Java Champions Brian Demers and Trisha Gee as they cover top challenges developers face when testing software, like dealing with long test cycles, slow and frustrating troubleshooting, a "forever war" with flaky tests, and the upcoming impact of AI-generated tests on projects.',
    eventType: 'Online',
    date: 'May 28, 2025',
    time: '08:00 PST',
    timeStamp: '2025-05-28T08:00:00-07:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-04-28/n3x235',
    zoomId: '81506689558',
    videoLink: 'https://youtu.be/aFkhj2ekohY',
    fullDescription:
      'Let\'s be honest: testing software is awful.\n\nAccording to GitHub, developers spend the same amount of time writing tests as they do writing new code (and an equal amount of time waiting for build and test cycles). Between writing tests and waiting for tests, who has time or strength to then troubleshoot a test failure?\n\nJoin this open discussion with Java Champions Trisha Gee and Brian Demers for an in-depth conversation about testing. \nWe\'ll talk about top challenges developers have to overcome, like regularly dealing with long test cycles, slow and frustrating troubleshooting, a "forever war" with flaky tests, the upcoming impact of AI-generated tests on projects, and the hit to developer morale and productivity around all of this.',
    discussionTopics: [
      'Problems with testing software (so many)',
      'Getting to the root cause of failures',
      'Can best practices even help?',
      'Demo: Solutions to reduce your pain',
    ],
    speakers: [
      {
        name: 'Trisha Gee',
        title: 'Lead Developer Advocate',
        company: 'Gradle',
        bio: 'Trisha is a Java Champion, software engineer, developer advocate and author who is passionate about developer productivity and happiness. She has expertise in Java high performance systems and dabbles with Open Source development.',
        headshotLink: 'trishaGee.png',
      },
      {
        name: 'Brian Demers',
        title: 'Developer Advocate',
        company: 'Gradle',
        bio: 'Brian Demers is a Developer Advocate at Gradle, a Java Champion, and an Apache Member who contributes to the Directory, Maven, and Shiro projects. He spends much of his day contributing to OSS projects by writing code, tutorials, blogs, and answering questions. In addition to typical software development, Brian also has a passion for fast builds and automation.\n\nAway from the keyboard, Brian is a beekeeper and can likely be found playing board games. You can find him on Twitter at https://twitter.com/briandemers.',
        headshotLink: 'brianDemers.png',
      },
    ],
    heroImageLink: 'testing-software-is-awful.jpg',
  },
  {
    slug: 'software-delivery-excellence-in-the-age-of-ai-06-26',
    title: 'Software Delivery Excellence in the age of AI: A Fireside Chat with Martin Odersky and Hans Dockter',
    description:
      "From build tooling to language design, we'll explore how we can stay productive as the JVM world absorbs the new wave of AI complexity.",
    eventType: 'Online',
    date: 'June 26, 2025',
    time: '08:00 PST',
    timeStamp: '2025-06-26T08:00:00-07:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity', 'Software Delivery', 'AI'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-05-19/n3xf5k',
    zoomId: '83587965109',
    videoLink: 'https://youtu.be/SkRk779vDus',
    fullDescription: (
      <>
        <p>
          What does it mean to be truly productive as a developer in the modern JVM ecosystem? In this candid fireside
          chat, Scala creator Martin Odersky and Gradle founder Hans Dockter sit down with Trisha Gee, Java Champion, to
          unpack the evolving landscape of developer productivity.
        </p>
        <br />
        <p>
          As AI-generated code becomes more prevalent, developers face new challenges: larger codebases, longer build
          times, and a growing gap between code and comprehension. What happens when code is technically correct but
          semantically opaque? How do we maintain quality, readability, and speed in a world of auto-generated tests and
          AI-assisted pipelines?
        </p>
        <br />
        <p>
          From build tooling to language design, this session explores how we can stay productive—and sane—as the JVM
          world absorbs this new wave of complexity. Bring your questions and join a timely conversation with the
          creators shaping the tools you use every day.
        </p>
      </>
    ),
    heroImageLink: 'software-delivery-excellence.png',
    discussionTopics: [
      'What are your observations regarding the effect of Gen AI on codebase quality, security, understandability?',
      'What are your predictions on the impact of Gen AI on software delivery pipelines?',
      'What are the backstories behind Gradle and Scala-what was the original goal of creating these technologies, and how has that changed over the last decade plus?',
      'How is AI poised to influence the design of Scala as a language and its focus on software quality, scalability and developer productivity?',
      "How is AI poised to influence Gradle's products and their commitment to developer productivity, toolchain observability and software delivery excellence?",
      'As new tooling and Gen AI begin to replace people for certain software development and delivery tasks, what are the skills that humans will need to sharpen to be productive and useful in the future?',
    ],
    speakers: [
      {
        name: 'Hans Dockter',
        title: 'Co-founder & CEO',
        company: 'Gradle',
        bio: 'Before founding Develocity, Hans spent many years as a developer and led numerous complex software projects for large organizations. In 2002, Hans started his career as a committer for the JBoss project and then founded the JBoss-IDE. From there, Hans spent multiple years as Senior Software Developer for Volkswagen and Krugle. This experience led Hans to start the open-source project, Gradle Build Tool, with Adam Murdoch in 2008. The success of the Gradle Build Tool project ultimately led to the development of Develocity®, launched in 2017.',
        headshotLink: 'hansDockter.png',
      },
      {
        name: 'Martin Odersky',
        title: 'Creator of Scala & Professor',
        company: 'EPFL',
        bio: 'Martin Odersky is the creator of the Scala programming language and a professor at EPFL in Lausanne, Switzerland. He has been working on programming languages for most of his career. He first studied structured and object-oriented programming as a PhD student of Niklaus Wirth, then fell in love with functional programming while working as a post doc at IBM and Yale. When Java came out, he started to add functional programming constructs to the new platform. This led to Pizza and GJ and eventually to Java 5 with generics. During that time he also developed javac, the current reference compiler for Java. Over the last 10 years, Martin worked on unifying object-oriented and functional programming in the Scala language. Scala quickly escaped from the research lab and became a popular open source tool and industrial language. He now oversees development of Scala as head of the programming group at EPFL and as academic director of the Scala center.',
        headshotLink: 'martinOdersky.png',
      },
      {
        name: 'Trisha Gee',
        title: 'Lead Developer Advocate',
        company: 'Gradle',
        bio: 'Trisha is a Java Champion, software engineer, developer advocate and author who is passionate about developer productivity and happiness. She has expertise in Java high performance systems and dabbles with Open Source development.',
        headshotLink: 'trishaGee.png',
      },
    ],
  },
  {
    slug: 'gradle-build-cache-07-25',
    title: 'Gradle Build Cache: Deliver faster by doing less',
    description:
      'Join experts from the Gradle Build Tool team to learn how to use build caching to reduce both local and CI build times.',
    eventType: 'Online',
    date: 'July 24, 2025',
    time: '08:00 PDT',
    timeStamp: '2025-07-24T08:00:00-07:00',
    locationShort: '',
    isFree: true,
    topics: ['Developer productivity', 'Acceleration', 'Gradle Build Tool'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-06-11/n3xslc',
    zoomId: '89839187704',
    fullDescription:
      "Fast software delivery means that your build performance matters—especially when you're running dozens of tests, compiling thousands of classes, or pushing changes through CI multiple times a day.\n\nBuild caching and related performance gains have made Gradle Build Tool one of the most popular tools in the JVM ecosystem and a default option in many tool stacks, from Spring Boot to Android. In 2025, Gradle's caching capabilities will continue to evolve.\n\nIn this talk, we'll discuss how Gradle's build cache can help you cut down your build times by reusing task outputs from previous runs. We'll review real-world examples from open source projects that are using a free Gradle-sponsored Develocity instance to leverage local and distributed caching. We'll also share pointers for developers looking to level up their Gradle Build Tool skills, such as how to get certified with the help of DPE University.",
    discussionTopics: [
      'The basics of local build caching—how it works, key aspects for build and test avoidance provided by Gradle, and how to analyze and maximize the cache hits.',
      'The tools provided by Gradle, including recently introduced features offering better analytics.',
      'Remote build caching with Develocity Build Cache, and how to share cached outputs across machines to speed up both CI pipelines and day-to-day development workflows.',
    ],
    speakers: [
      {
        name: 'Laura Kassovic',
        title: 'Technical Writer',
        company: 'Gradle',
        bio: 'Laura Kassovic is a Technical Writer at Develocity, where she specializes in documentation and developer education. Laura is passionate about bridging the gap between complex technical concepts and user-friendly documentation. Her experience spans from hands-on hardware development to crafting comprehensive guides that enhance developer onboarding and productivity. Prior to her role at Gradle, Laura co-founded MbientLab and Beacon3D, companies focused on wearable technology and 3D sensing solutions. Her work has been recognized in the Arm Innovator Series, highlighting her contributions to the development of Bluetooth-enabled sensor platforms and their applications in various industries.',
        headshotLink: 'lauraKassovic.png',
      },
      {
        name: 'Akshay Dayal',
        title: 'Technical Trainer',
        company: 'Gradle',
        bio: 'Akshay is a Technical Trainer here at Gradle. His previous experience includes software development and build engineering.',
        headshotLink: 'akshayDayal.png',
      },
    ],
    heroImageLink: 'gradle-build-cache-webinar-jul2025-2.jpg',
    videoLink: 'https://www.youtube.com/watch?v=G8N7xt5gHec',
  },
  {
    title: 'Join Gradle and JFrog at swampUP',
    description: 'JFrog and Gradle are teaming up to bring the next evolution of Continuous GRC for DevOps!',
    eventType: eventTypes.inPerson,
    date: 'September 8-9, 2025',
    time: '09:00 PDT',
    timeStamp: '2025-09-08T09:00:00+00:00',
    locationShort: 'Napa, California',
    isFree: false,
    externalLink: '/jfrog-gradle-swampup-announce/',
  },
  {
    slug: 'intellij-plugin-develocity-ai-troubleshooting-11-25',
    title: 'Using IntelliJ with Develocity AI for faster troubleshooting',
    description:
      'Explore the use of the free Develocity IntelliJ plugin, along with Develocity AI features, for rapidly troubleshooting failures and performance issues within the comfort of your IDE.',
    eventType: 'Online',
    date: 'November 13, 2025',
    time: '08:00 PST / 17:00 CEST',
    timeStamp: '2025-11-13T08:00:00-08:00',
    locationShort: '',
    isFree: true,
    topics: ['Troubleshooting', 'Android', 'Gradle Build Tool', 'Metrics', 'AI'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-10-09/n416mr',
    zoomId: '85277092880',
    fullDescription:
      "AI is accelerating code and test creation, leading to larger changes and rapidly expanding codebases. This growth, however, intensifies the complexities of troubleshooting.\n\nTo keep up with AI, we need faster feedback cycles, as well as actionable insights. Inspired by DORA, we measure this with Developer-local or CI-local Time To Restore (TTR), for a failing local or CI build, respectively.\n\nOnly AI-powered troubleshooting can help streamline AI-powered development, and what better place to observe and troubleshoot builds and tests than inside your IDE, like IntelliJ IDEA or Android Studio?\n\nIn this presentation with Laurent Ploix and Trisha Gee, we'll explore the use of the free Develocity IntelliJ plugin, along with Develocity AI features, for rapidly troubleshooting failures and performance issues within the comfort of your IDE.",
    heroImageLink: 'EVENT_intellijPluginWebinar-NOV2025.jpg',
    discussionTopics: [
      'Explore the Develocity IntelliJ plugin features and use cases',
      'How to access AI-powered Failure Analytics in your project with Build Scan®',
      'Sneak preview: Using AI to troubleshoot AI with the upcoming Develocity MCP server and Agentic AI',
    ],
    speakers: [
      {
        name: 'Laurent Ploix',
        title: 'Head of Product',
        company: 'Gradle',
        bio: "Laurent is the Head of Product at Gradle. He joined Spotify in 2014 as a Product Manager for Continuous Integration, coinciding with the company's rapid growth. He then led the Platform Insights team, which focused on understanding productivity through a multi-disciplinary approach combining data science, data engineering, and user research.",
        headshotLink: 'laurentPloix.png',
      },
      {
        name: 'Trisha Gee',
        title: 'Head of Advocacy',
        company: 'Gradle',
        bio: "Trisha Gee is a Java Champion, author, and internationally recognized speaker with over two decades of experience in software development. Known for her deep expertise in Java, high-performance systems, and developer productivity, Trisha has worked as a developer and leader in organizations ranging from startups to global enterprises. She's passionate about sharing knowledge and helping developers write more expressive and efficient code.\n\nTrisha is the author of multiple technical books, including Head First Java (3rd Edition) and Getting to Know IntelliJ IDEA, and she frequently contributes to developer communities through blogs, webinars, and international conferences. She's also a strong advocate for grassroots learning and regularly supports local user groups and meetups to help developers connect, grow, and thrive.\n\nWhen she's not writing or coding, she's championing inclusive practices and mentoring the next generation of developers.",
        headshotLink: 'trishaGee.png',
      },
    ],
    videoLink: 'https://youtu.be/RwImFGdqnpo',
  },
  {
    title: 'Code Maintenance at Scale with AI - London',
    description:
      'Join Moderne’s in person panel with leaders from Gradle, Google, Meta, DX, and Moderne on how AI is transforming the way enterprises maintain, remediate, and modernize massive codebases.',
    eventType: 'In-person',
    date: 'December 10, 2025',
    time: '14:00 - 18:00 GMT',
    timeStamp: '2025-12-10T14:00:00+00:00',
    locationShort: 'London, UK',
    locationFull: 'Morgan Stanley, 25 Cabot Square, Canary Wharf, London',
    isFree: true,
    topics: ['AI'],
  },
  {
    slug: 'develocity-universal-cache-01-26',
    title: 'CI without the wait: Scale throughput with Develocity Universal Cache',
    description:
      'GenAI code volume is overloading CI, often with redundant work. The clear path back to CI efficiency is comprehensive caching that spans the entire build life cycle.',
    eventType: 'Online',
    date: 'January 28, 2026',
    time: '08:00 - 09:00 PST',
    timeStamp: '2026-01-28T08:00:00-08:00',
    videoLink: 'https://youtu.be/bS2p2xUAI5c',
    locationShort: '',
    isFree: true,
    topics: ['AI', 'Acceleration', 'Developer productivity', 'Software Delivery'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2025-12-15/n42hch',
    zoomId: '81805423710',
    fullDescription:
      "Whether it's the 5x surge in commits driven by GenAI or the increasing complexity of modern applications, CI infrastructure is under unprecedented stress. A major source of inefficiency is redundant work: re-downloading dependencies, redoing computations, regenerating the same code. One customer discovered that they wasted over 5 days of wall-clock time in a single week just downloading artifacts!\n\nThe clear path back to CI efficiency is comprehensive, universal caching that spans the entire build life cycle. We're excited to introduce Develocity Universal Cache, an observable, highly-available, and globally-distributed caching layer.\n\nIn this webinar, you'll learn about the causes of CI slow-down and then we'll use a series of use cases and demos to show you how Develocity Universal Cache efficiently stores dependencies, JVMs, setup tasks/goals, and build outputs securely across geographies.",
    heroImageLink: 'EVENT_universalCacheWebinar-JAN2026.png',
    discussionTopics: [
      'Tracking artifacts, fixing root-cause cache misses, and applying artifact-level caching to cut fetch time, reduce failures/timeouts, and lower network bandwidth',
      'Caching outputs of setup/config steps to eliminate drift, stabilize pipelines, and decrease re-tries and CI minutes during environment initialization',
      'Reusing previously computed build/test outputs with provenance, reducing repeat execution, improving build times, and increasing overall pipeline reliability at scale',
    ],
    speakers: [
      {
        name: 'Brian Demers',
        title: 'Developer Advocate',
        company: 'Gradle',
        bio: 'Brian Demers is a Developer Advocate at Gradle, a Java Champion, and an Apache Member who contributes to the Directory, Maven, and Shiro projects. He spends much of his day contributing to OSS projects by writing code, tutorials, blogs, and answering questions. In addition to typical software development, Brian also has a passion for fast builds, security, and automation.\n\nAway from the keyboard, Brian is a beekeeper and can likely be found playing board games. You can find him online at https://bsky.app/profile/bdemers.io.',
        headshotLink: 'brianDemers.png',
      },
      {
        name: 'Mirco Dotta',
        title: 'Sr. Engineering Manager',
        company: 'Gradle',
        bio: "Mirco Dotta is a Senior Engineering Manager in the Develocity organization at Gradle, where he leads the creation of cutting-edge solutions for toolchain observability and accelerating software delivery.\n\nMirco's professional interests center on building and elevating product-focused engineering teams. Before joining Gradle, Mirco was an entrepreneur in the Developer Productivity Engineering space. As the co-founder of Triplequote, he led the creation of the Hydra parallel Scala compiler, a venture that was acquired by Gradle. In his previous life, he contributed to various open-source projects in the Scala ecosystem, most notably the Scala compiler and the Play Framework.",
        headshotLink: 'mircoDotta.png',
      },
    ],
  },
  {
    slug: 'automating-governance-build-release-gradle-jfrog-02-26',
    title: 'Automating governance from build to release with Develocity and JFrog',
    description:
      "Explore how the emerging practice of Continuous Governance, Risk, and Compliance (GRC) ensures the integrity of the build process in today's AI-powered development world.",
    eventType: 'Online',
    date: 'February 19, 2026',
    time: '08:00 PST',
    timeStamp: '2026-02-19T08:00:00-08:00',
    videoLink: 'https://www.youtube.com/watch?v=cvNvNHvN8vk',
    isFree: true,
    topics: ['Software Delivery', 'GRC'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2026-01-15/n42x59',
    zoomId: '83594198049',
    heroImageLink: 'EVENT_develocityJfrogGrcWebinar-FEB2026.webp',
    fullDescription:
      'As AI-accelerated development floods the software supply chain with a tsunami of code, the manual effort required to prove where an artifact came from, who built it, and which tools were used has become physically impossible to sustain. Traditional security scans tell you what\'s inside your code, but they leave a dangerous blind spot: the chain of custody and integrity of the build process itself.\n\nJoin Develocity and JFrog to explore how the emerging practice of Continuous Governance, Risk, and Compliance (GRC)—a.k.a. "DevGovOps"—bridges this gap. We\'ll demonstrate how the integration between Develocity Provenance Governor from Develocity and JFrog AppTrust creates a continuous "system of record" for your software.\n\nBy capturing signed provenance data from deep within the software toolchain and converting it into immutable evidence, organizations can finally automate their compliance gates and ensure that only verified software reaches production.',
    discussionTopics: [
      "Why scanning isn't governing, and how to solve the lack of toolchain observability.",
      "Connecting Develocity Provenance Governor signed build artifact/toolchain telemetry directly to AppTrust's evidence collection.",
      'Setting policy-based gates that automatically block artifacts built with unauthorized tools, dependencies, and environments.',
      'Live demo of real-time violation gating that replaces late-stage, manual audit failures.',
    ],
    speakers: [
      {
        name: 'Brian Demers',
        title: 'Developer Advocate',
        company: 'Develocity',
        bio: 'Brian Demers is a Developer Advocate at Develocity, a Java Champion, and an Apache Member who contributes to the Directory, Maven, and Shiro projects. He spends much of his day contributing to OSS projects by writing code, tutorials, blogs, and answering questions. In addition to typical software development, Brian also has a passion for fast builds, security, and automation.\n\nAway from the keyboard, Brian is a beekeeper and can likely be found playing board games. You can find him online at https://bsky.app/profile/bdemers.io.',
        headshotLink: 'brianDemers.png',
      },
      {
        name: 'Sophie Starchenko',
        title: 'Senior Product Manager',
        company: 'JFrog AppTrust',
        bio: 'Sophie Starchenko is a Senior Product Manager at JFrog AppTrust, focusing on software supply chain integrity, GRC, and compliance. With over 10 years of experience in product leadership across security and other technology domains, she helps organizations navigate demanding regulatory standards with confidence. She is passionate about transforming complex challenges into seamless, automated solutions.',
        headshotLink: 'sophieStarchenko.png',
      },
    ],
  },
  {
    slug: 'modernizing-apache-kafka-ci-builds-develocity-github-actions-03-26',
    title: 'Lessons learned modernizing Apache Kafka CI builds with Develocity and GitHub Actions',
    description:
      "Get the lessons-learned from the migration from Apache Kafka's migration from Jenkins to GitHub Actions, including how Develocity helped quantify performance gains and prove ROI.",
    eventType: 'Online',
    date: 'March 12, 2026',
    time: '09:00 - 10:00 PDT',
    timeStamp: '2026-03-12T09:00:00-07:00',
    locationShort: '',
    isFree: true,
    topics: ['Acceleration', 'Metrics'],
    pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2026-02-11/n43ddc',
    zoomId: '87898635148',
    heroImageLink: 'EVENT_apacheKafkaWebinar-MAR2026.png',
    videoLink: 'https://www.youtube.com/watch?v=AwR_LlUgDdc',
    fullDescription: `You've probably heard the phrase, "You can’t improve what you can't measure." This was true for CI build and test times for the popular OSS streaming platform, Apache Kafka. And this very lack of observability finally led the project committers to hit their breaking point.

As a result, Apache Kafka underwent a major overhaul in 2025—going from a nearly defunct 8-hour build cycle to evergreen 2-hour builds. All by modernizing its CI infrastructure away from Jenkins to GitHub Actions.

Join David Arthur, Apache Kafka PMC and Senior Staff Software Engineer at Confluent, as he shares his lessons-learned from the migration, including how Develocity helped quantify performance gains and prove their modernization ROI.`,
    discussionTopics: [
      'Using toolchain observability to help manage the CI migration from Jenkins to GitHub Actions, plus some custom workflows the team developed',
      'Tips for leveraging Build Cache to save hours of wasted time by avoiding unnecessary task re-runs',
      'Employing Flaky Test Detection to resolve the ever-present specter of flaky tests in a project with over 32,000 tests and a 7-hour serial runtime',
    ],
    speakers: [
      {
        name: 'David Arthur',
        title: 'Apache Kafka PMC',
        company: 'Confluent',
        bio: 'David Arthur is a Senior Staff Software Engineer at Confluent and an Apache Kafka project committer. David is a technology generalist with expertise in Java and Python. He has a particular interest in distributed systems, search, software architecture/design, and the overall software development lifecycle.',
        headshotLink: 'davidArthur.png',
      },
    ],
  },
  buildCachingOptimizerWebinar,
];
